import { alignmentWalkAtom, alignmentsAtom, selectedAlignmentKeyAtom } from '@/atoms/alignment.atom';
import {
  formatStation,
  gradeAtStation,
  internalToDisplay,
  pointAtStation,
  type Alignment,
} from '@/core/alignment';
import { unitsPerPixel } from '@/lib/three/unitsPerPixel';
import { Billboard, Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { CrosshairMarker } from './CrosshairMarker';

export const HIGHLIGHT = '#ffb300';
const KEYPOINT = '#ff5722';
/** Tick spacing in DISPLAY metres (so ticks line up with staStart / equations). */
const MINOR_STEP = 20;
const MAJOR_STEP = 100;
/** Minimum on-screen gap between two station labels. */
const MIN_LABEL_PX = 80;
/** Safety valve — a pathological alignment/equation set must not spin forever. */
const MAX_TICKS = 20000;

/** Reused inside `useFrame` so label layout allocates nothing per frame. */
const _tmpWorld = new THREE.Vector3();

export interface AlignmentTick {
  /** Internal station. */
  station: number;
  /** True when the tick falls on a MAJOR_STEP display station (gets a label). */
  major: boolean;
}

/** `alignment.key`s already warned about, so the console isn't spammed per render. */
const _tickTruncationWarned = new Set<string>();

/**
 * Station ticks for one alignment, generated in DISPLAY space so they line up
 * with `staStart` and any station equations.
 *
 * Display stations are NOT monotonic in the presence of a backward equation
 * (e.g. `staStart = 1000`, `length = 500`, equation `{ staInternal: 200,
 * staAhead: 100 }` covers display `[1000, 1200] ∪ [100, 400]`), so scanning
 * one `min..max` display range would miss almost everything. Instead we walk
 * each *equation region* — bounds `[0, eq0.staInternal, …, length]` — where
 * the display shift is constant, and union the results.
 *
 * Two regions' display intervals can also OVERLAP (a partial backward
 * equation, e.g. `{ staInternal: 200, staAhead: 150 }` → `[0, 200]` and
 * `[150, 450]`). That is why the display → internal mapping here is the
 * REGION-LOCAL inverse `s = lo + (d - dispLo)` and not the global
 * `displayToInternal()`, which resolves a display station to the FIRST
 * matching region and would therefore drop every tick of the later region
 * inside the overlap.
 *
 * Region membership is half-open — `[lo, hi)` — except the last region, which
 * is `[lo, hi]`. So a station sitting exactly on an equation belongs to the
 * region that STARTS there (the one the equation introduces), matching
 * `internalToDisplay()`, which applies an equation at `station === staInternal`.
 *
 * Exported so the (fiddly) equation arithmetic can be unit-tested without r3f.
 */
export function computeTickStations(a: Alignment): AlignmentTick[] {
  const bounds = [0, ...a.equations.map((e) => e.staInternal), a.length];
  /** internal station (rounded to µm) → major flag. Dedupes region overlaps. */
  const byStation = new Map<number, boolean>();
  let truncated = false;

  outer: for (let r = 0; r < bounds.length - 1; r++) {
    const lo = Math.min(Math.max(bounds[r], 0), a.length);
    const hi = Math.min(Math.max(bounds[r + 1], 0), a.length);
    if (hi < lo) continue;
    // Within a region the shift is constant, so display is affine in station.
    const dispLo = internalToDisplay(a, lo);
    const dispHi = dispLo + (hi - lo);
    const first = Math.ceil(dispLo / MINOR_STEP) * MINOR_STEP;
    const isLast = r === bounds.length - 2;
    for (let i = 0; ; i++) {
      // Index-based so accumulated float error never drifts the major test.
      const d = first + i * MINOR_STEP;
      if (d > dispHi + 1e-9) break;
      // Region-local inverse — see the note on overlapping regions above.
      const raw = lo + (d - dispLo);
      if (raw < lo - 1e-9) continue;
      if (isLast ? raw > hi + 1e-9 : raw >= hi - 1e-9) continue;
      const s = Math.min(Math.max(raw, 0), a.length);
      const key = Math.round(s * 1e6) / 1e6;
      const major = Math.abs(d / MAJOR_STEP - Math.round(d / MAJOR_STEP)) < 1e-6;
      const prev = byStation.get(key);
      if (prev === undefined) {
        if (byStation.size >= MAX_TICKS) {
          truncated = true;
          break outer;
        }
        byStation.set(key, major);
      } else if (major && !prev) {
        byStation.set(key, true);
      }
    }
  }

  if (truncated && !_tickTruncationWarned.has(a.key)) {
    _tickTruncationWarned.add(a.key);
    console.warn(
      `[AlignmentOverlay] station ticks truncated at ${MAX_TICKS} for alignment "${a.name}" (${a.key}); the drawn tick range is incomplete.`,
    );
  }

  return [...byStation.entries()]
    .map(([station, major]) => ({ station, major }))
    .sort((x, y) => x.station - y.station);
}

/** Screen-space-sized billboard text pinned above a point in the parent group's local space. */
const StationLabel = ({
  position,
  text,
  offsetPx = 10,
  color = '#ffffff',
}: {
  position: [number, number, number];
  text: string;
  offsetPx?: number;
  color?: string;
}) => {
  const ref = useRef<THREE.Group>(null);
  useFrame(({ camera, size }) => {
    const g = ref.current;
    if (!g) return;
    // WORLD position — the parent group sits at UTM scale (floating origin).
    g.getWorldPosition(_tmpWorld);
    const upp = unitsPerPixel(camera, size, _tmpWorld);
    g.scale.setScalar(Math.max(upp * 12, 1e-4)); // 12 px cap height (fontSize={1})
    g.position.set(position[0], position[1], position[2] + offsetPx * upp);
  });
  return (
    <group ref={ref} position={position}>
      <Billboard>
        <Text
          fontSize={1}
          color={color}
          outlineColor='black'
          outlineWidth={0.08}
          outlineBlur={0.05}
          depthOffset={Number.MIN_SAFE_INTEGER}
          renderOrder={Number.MAX_SAFE_INTEGER}
          anchorX='center'
          anchorY='bottom'>
          {text}
        </Text>
      </Billboard>
    </group>
  );
};

/**
 * Highlighted polyline + station ticks + station labels + key-point markers
 * for one alignment. Everything is built relative to the alignment's first
 * vertex and the whole thing is hung off a group positioned at that origin,
 * so nothing suffers float32 jitter at UTM scale.
 */
const AlignmentGeometry = ({ alignment, withLabels }: { alignment: Alignment; withLabels: boolean }) => {
  const origin = useMemo(
    () => new THREE.Vector3(alignment.points[0], alignment.points[1], alignment.points[2]),
    [alignment],
  );

  // ---- highlighted centreline -------------------------------------------
  const line = useMemo(() => {
    const n = alignment.points.length / 3;
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      arr[i * 3] = alignment.points[i * 3] - origin.x;
      arr[i * 3 + 1] = alignment.points[i * 3 + 1] - origin.y;
      arr[i * 3 + 2] = alignment.points[i * 3 + 2] - origin.z;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    const mat = new THREE.LineBasicMaterial({ color: HIGHLIGHT, depthTest: false, transparent: true, opacity: 0.95 });
    const obj = new THREE.Line(geom, mat);
    obj.renderOrder = 999;
    obj.frustumCulled = false;
    return obj;
  }, [alignment, origin]);
  useEffect(
    () => () => {
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    },
    [line],
  );

  // ---- tick stations (DISPLAY space, so they respect staStart/equations) --
  const ticks = useMemo(() => computeTickStations(alignment), [alignment]);

  // One geometry + one Float32Array for the whole life of this alignment; the
  // tick half-lengths are rewritten in place when the zoom bucket changes.
  const tickGeom = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(ticks.length * 6), 3));
    return geom;
  }, [ticks]);
  useEffect(() => () => tickGeom.dispose(), [tickGeom]);

  const [labelEvery, setLabelEvery] = useState(1);
  const lastBucket = useRef<number | null>(null);
  const lastLabelUpdate = useRef(0);
  // A new alignment (or tick set) invalidates the cached zoom bucket.
  useEffect(() => {
    lastBucket.current = null;
  }, [tickGeom, alignment, origin]);

  useFrame(({ camera, size, clock }) => {
    const upp = unitsPerPixel(camera, size, origin);
    const bucket = Math.round(Math.log2(Math.max(upp, 1e-6)) * 2);
    if (bucket !== lastBucket.current) {
      lastBucket.current = bucket;
      const minorLen = 6 * upp;
      const majorLen = 12 * upp;
      const attr = tickGeom.getAttribute('position') as THREE.BufferAttribute;
      const arr = attr.array as Float32Array;
      for (let i = 0; i < ticks.length; i++) {
        const t = ticks[i];
        const p = pointAtStation(alignment, t.station);
        const len = t.major ? majorLen : minorLen;
        // Left normal of the 2D heading (tx,ty is a unit vector in XY).
        const nx = -p.ty;
        const ny = p.tx;
        const h = Math.hypot(nx, ny) || 1;
        const ox = (nx / h) * len;
        const oy = (ny / h) * len;
        const px = p.x - origin.x;
        const py = p.y - origin.y;
        const pz = p.z - origin.z;
        const o = i * 6;
        arr[o] = px - ox;
        arr[o + 1] = py - oy;
        arr[o + 2] = pz;
        arr[o + 3] = px + ox;
        arr[o + 4] = py + oy;
        arr[o + 5] = pz;
      }
      attr.needsUpdate = true;
      // No computeBoundingSphere(): the lineSegments is frustumCulled={false},
      // so the bounding sphere is never read — it would be two dead O(n) passes.
    }
    if (withLabels) {
      const now = clock.elapsedTime;
      if (now - lastLabelUpdate.current > 0.2) {
        lastLabelUpdate.current = now;
        const pxPerMajor = MAJOR_STEP / Math.max(upp, 1e-6);
        const every = Math.max(1, Math.ceil(MIN_LABEL_PX / Math.max(pxPerMajor, 1e-6)));
        // Guarded: only fires when the thinning factor actually changes.
        setLabelEvery((prev) => (prev === every ? prev : every));
      }
    }
  });

  const labelled = useMemo(() => {
    if (!withLabels) return [];
    const majors = ticks.filter((t) => t.major);
    return majors
      .filter((_, i) => i % labelEvery === 0)
      .map((t) => {
        const p = pointAtStation(alignment, t.station);
        return {
          station: t.station,
          local: [p.x - origin.x, p.y - origin.y, p.z - origin.z] as [number, number, number],
          text: formatStation(internalToDisplay(alignment, t.station), 0),
        };
      });
  }, [withLabels, ticks, labelEvery, alignment, origin]);

  const keyPoints = useMemo(
    () =>
      alignment.keyPoints
        .filter((k) => k.kind !== 'START' && k.kind !== 'END' && k.kind !== 'VERTEX')
        .map((k, i) => {
          const p = pointAtStation(alignment, k.station);
          return {
            id: `${k.kind}-${i}`,
            kind: k.kind,
            local: [p.x - origin.x, p.y - origin.y, p.z - origin.z] as [number, number, number],
          };
        }),
    [alignment, origin],
  );

  return (
    <group position={origin}>
      <primitive object={line} />
      <lineSegments renderOrder={999} frustumCulled={false}>
        <primitive object={tickGeom} attach='geometry' />
        <lineBasicMaterial color={HIGHLIGHT} depthTest={false} transparent opacity={0.95} />
      </lineSegments>
      {labelled.map((l) => (
        <StationLabel key={l.station} position={l.local} text={l.text} />
      ))}
      {keyPoints.map((k) => (
        <group key={k.id} position={k.local}>
          <CrosshairMarker position={[0, 0, 0]} color={KEYPOINT} screenSize={0.01} />
          <StationLabel position={[0, 0, 0]} text={k.kind} offsetPx={14} color={KEYPOINT} />
        </group>
      ))}
    </group>
  );
};

/** Walk marker: crosshair + perpendicular bar + station/Z/grade readout. */
const WalkMarker = ({ alignment, station }: { alignment: Alignment; station: number }) => {
  const p = useMemo(() => pointAtStation(alignment, station), [alignment, station]);
  const origin = useMemo(() => new THREE.Vector3(p.x, p.y, p.z), [p]);

  // Unit-length bar along the left normal; the group scale does the sizing.
  const bar = useMemo(() => {
    const nx = -p.ty;
    const ny = p.tx;
    const h = Math.hypot(nx, ny) || 1;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-nx / h, -ny / h, 0, nx / h, ny / h, 0]), 3),
    );
    const obj = new THREE.Line(geom, new THREE.LineBasicMaterial({ color: HIGHLIGHT, depthTest: false }));
    obj.renderOrder = 1000;
    obj.frustumCulled = false;
    return obj;
  }, [p]);
  useEffect(
    () => () => {
      bar.geometry.dispose();
      (bar.material as THREE.Material).dispose();
    },
    [bar],
  );

  useFrame(({ camera, size }) => {
    // `origin` is already the marker's world position (the group sits on it).
    const upp = unitsPerPixel(camera, size, origin);
    bar.scale.setScalar(Math.max(upp * 20, 1e-4));
  });

  const label = `${formatStation(internalToDisplay(alignment, station))}  Z ${p.z.toFixed(2)}  ${(
    gradeAtStation(alignment, station) * 100
  ).toFixed(1)} %`;

  return (
    <group position={origin}>
      <CrosshairMarker position={[0, 0, 0]} color={HIGHLIGHT} screenSize={0.018} />
      <primitive object={bar} />
      <StationLabel position={[0, 0, 0]} text={label} offsetPx={22} />
    </group>
  );
};

/**
 * In-scene overlay for the walked and/or selected alignment. Renders nothing
 * when neither is set.
 */
export const AlignmentOverlay = () => {
  const alignments = useAtomValue(alignmentsAtom);
  const walk = useAtomValue(alignmentWalkAtom);
  const selectedKey = useAtomValue(selectedAlignmentKeyAtom);
  const walked = walk ? alignments.get(walk.key) : undefined;
  const selected = selectedKey && selectedKey !== walk?.key ? alignments.get(selectedKey) : undefined;
  if (!walked && !selected) return null;
  return (
    <>
      {walked && <AlignmentGeometry key={walked.key} alignment={walked} withLabels />}
      {selected && <AlignmentGeometry key={selected.key} alignment={selected} withLabels={false} />}
      {walked && walk && <WalkMarker alignment={walked} station={walk.station} />}
    </>
  );
};
